import { TICK_RATE } from './constants';
import { GameState, DASState } from './state';
import { createGame, updateGame } from './game';
import { initInput, getInputState, createDASState, getRawKeyState } from './input';
import { initRenderer, render, renderMultiplayer, renderMenu, setMultiplayerLayout, setPlayerLabels } from './renderer';
import { NetworkClient, NetworkState } from './network';
import { GameStateMsg, MatchInfo, ReadyStatus } from './protocol';

type AppMode = 'menu' | 'single' | 'multi';

let appMode: AppMode = 'menu';
let gameState: GameState;
let dasState: DASState;
let lastTime = 0;
let accumulator = 0;
const FRAME_TIME = 1000 / TICK_RATE;

// Multiplayer state
let networkClient: NetworkClient | null = null;
let networkState: NetworkState = 'disconnected';
let multiplayerState: GameStateMsg | null = null;
let countdownTicks = 0;
let winnerId = -1;
let myPlayerId = -1;
let lobbyPlayerCount = 0;
let myReady = false;
let opponentReady = false;
let helpOpen = false;
let helpOverlay: HTMLElement | null = null;
let helpButton: HTMLElement | null = null;
let helpClose: HTMLElement | null = null;

function init(): void {
  initInput();
  initRenderer();
  initHelp();
  setMultiplayerLayout(false);
  setPlayerLabels(-1);

  // Handle menu/mode selection
  document.addEventListener('keydown', (e) => {
    if (helpOpen) {
      if (e.code === 'Escape' || e.code === 'KeyH' || e.code === 'Slash') {
        setHelpOpen(false);
        e.preventDefault();
      }
      return;
    }

    if (e.code === 'KeyH' || e.code === 'Slash') {
      setHelpOpen(true);
      e.preventDefault();
      return;
    }

    if (appMode === 'menu') {
      if (e.code === 'Digit1') {
        startSinglePlayer();
      } else if (e.code === 'Digit2') {
        startMultiplayer();
      }
    } else if (appMode === 'single' && gameState.gameOver) {
      if (e.code === 'KeyR') {
        gameState = createGame();
        dasState = createDASState();
      } else if (e.code === 'Escape') {
        appMode = 'menu';
        setMultiplayerLayout(false);
        setPlayerLabels(-1);
      }
    } else if (appMode === 'multi') {
      if (e.code === 'Escape') {
        if (networkClient) {
          networkClient.disconnect();
        }
        appMode = 'menu';
        setMultiplayerLayout(false);
        setPlayerLabels(-1);
      } else if ((networkState === 'matched' || networkState === 'finished') && e.code === 'Space') {
        networkClient?.sendReady();
      }
    }
  });

  requestAnimationFrame(gameLoop);
}

function startSinglePlayer(): void {
  appMode = 'single';
  gameState = createGame();
  dasState = createDASState();
  setMultiplayerLayout(false);
  setPlayerLabels(-1);
}

function startMultiplayer(): void {
  appMode = 'multi';
  multiplayerState = null;
  countdownTicks = 0;
  winnerId = -1;
  myPlayerId = -1;
  lobbyPlayerCount = 0;
  myReady = false;
  opponentReady = false;
  setMultiplayerLayout(true);
  setPlayerLabels(-1);

  networkClient = new NetworkClient({
    onStateChange: (state) => {
      networkState = state;
    },
    onMatchFound: (info: MatchInfo) => {
      myPlayerId = info.playerId;
      setPlayerLabels(myPlayerId);
      console.log('Match found! Player ID:', info.playerId);
    },
    onGameState: (state: GameStateMsg) => {
      multiplayerState = state;
    },
    onCountdown: (ticks: number) => {
      countdownTicks = ticks;
    },
    onGameOver: (winner: number) => {
      winnerId = winner;
    },
    onLobbyStatus: (count: number) => {
      lobbyPlayerCount = count;
    },
    onReadyStatus: (status: ReadyStatus) => {
      if (myPlayerId === 0) {
        myReady = status.player0Ready;
        opponentReady = status.player1Ready;
      } else if (myPlayerId === 1) {
        myReady = status.player1Ready;
        opponentReady = status.player0Ready;
      }
    },
  });

  networkClient.connect(getServerUrl());
}

function gameLoop(currentTime: number): void {
  if (lastTime === 0) {
    lastTime = currentTime;
  }

  const deltaTime = currentTime - lastTime;
  lastTime = currentTime;
  accumulator += deltaTime;

  if (appMode === 'menu') {
    renderMenu();
  } else if (appMode === 'single') {
    // Fixed timestep updates
    if (!helpOpen) {
      while (accumulator >= FRAME_TIME) {
        const input = getInputState();
        updateGame(gameState, input, dasState);
        accumulator -= FRAME_TIME;
      }
    } else {
      accumulator = 0;
    }
    render(gameState);
  } else if (appMode === 'multi') {
    if (!helpOpen) {
      while (accumulator >= FRAME_TIME) {
        const keys = getRawKeyState();
        if (networkState === 'playing' && networkClient) {
          networkClient.sendInput(
            keys.left,
            keys.right,
            keys.rotateCW,
            keys.rotateCCW,
            keys.softDrop,
            keys.hardDrop,
            keys.hold,
            keys.powerupSlot
          );
        }
        accumulator -= FRAME_TIME;
      }
    } else {
      accumulator = 0;
    }
    renderMultiplayer(networkState, multiplayerState, myPlayerId, countdownTicks, winnerId, lobbyPlayerCount, myReady, opponentReady);
  }

  requestAnimationFrame(gameLoop);
}

init();

function getServerUrl(): string {
  const params = new URLSearchParams(window.location.search);
  const host = params.get('host') || 'battfront-production.up.railway.app';
  const port = params.get('port');
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';

  // Only append port if explicitly provided (Railway URLs don't need ports)
  if (port) {
    return `${scheme}://${host}:${port}`;
  }
  return `${scheme}://${host}`;
}

function initHelp(): void {
  helpOverlay = document.getElementById('help-overlay');
  helpButton = document.getElementById('help-button');
  helpClose = document.getElementById('help-close');

  if (helpButton) {
    helpButton.addEventListener('click', () => {
      setHelpOpen(true);
    });
  }

  if (helpClose) {
    helpClose.addEventListener('click', () => {
      setHelpOpen(false);
    });
  }

  if (helpOverlay) {
    helpOverlay.addEventListener('click', (e) => {
      if (e.target === helpOverlay) {
        setHelpOpen(false);
      }
    });
  }
}

function setHelpOpen(open: boolean): void {
  helpOpen = open;
  if (!helpOverlay) return;
  helpOverlay.classList.toggle('hidden', !open);
  helpOverlay.setAttribute('aria-hidden', open ? 'false' : 'true');
}
